import { useState, useRef } from 'react'
import moment from 'moment'
import MomentUtils from '@date-io/moment'

import { makeStyles } from '@material-ui/core'
import { MuiPickersUtilsProvider, Calendar } from '@material-ui/pickers'
import TextField from '@material-ui/core/TextField'
import Popover from '@material-ui/core/Popover'
import Button from '@material-ui/core/Button'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'

import { useBoolean } from 'hooks'

const useStyles = makeStyles(theme => ({
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.5rem 1rem',
        fontSize: '1rem',
        fontWeight: '500',
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.primary.main
    },
    inputWrapper: {
        margin: '0 0.5rem',
        maxWidth: '7.5rem',
        '& input': {
            textAlign: 'center'
        }
    }
}))

const isDateInRange = (date, minDate, maxDate) =>
    (minDate && moment(minDate).startOf('day').isSameOrBefore(date)) ||
    (maxDate && moment(maxDate).startOf('day').isSameOrAfter(date))

export const DatePicker = ({ minDate, maxDate, value, onChange }) => {
    const initialDate = (moment(value) || moment()).startOf('day') // To avoid time bugs
    const anchorRef = useRef(null)
    const [calendarDate, changeCalendarDate] = useState(initialDate)
    const showCalendar = useBoolean(false)
    const classes = useStyles()

    const applyDate = (date, shouldClose = true) => {
        const formattedDate = moment(date).startOf('day')
        changeCalendarDate(date)
        if (isDateInRange(formattedDate, minDate, maxDate)) {
            onChange(formattedDate)
        }
        if (shouldClose) {
            showCalendar.setFalse()
        }
    }

    return (
        <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils}>
            <TextField
                readOnly
                value={moment(value).format('DD MMM, YYYY')}
                className={classes.inputWrapper}
                onClick={() => {
                    showCalendar.setTrue()
                }}
                ref={anchorRef}
            />

            <Popover
                open={showCalendar.isTrue}
                anchorEl={anchorRef?.current}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <ClickAwayListener
                    onClickAway={() => {
                        showCalendar.setFalse()
                    }}
                >
                    <div>
                        <div className={classes.toolbar}>
                            {moment(value).format('ddd DD MMM, YYYY')}
                            <Button
                                color="default"
                                onClick={() => applyDate(moment().startOf('day'), false)}
                            >
                                Today
                            </Button>
                        </div>
                        <Calendar
                            date={calendarDate}
                            onChange={applyDate}
                            disableFuture
                            minDate={minDate}
                            maxDate={maxDate}
                        />
                    </div>
                </ClickAwayListener>
            </Popover>
        </MuiPickersUtilsProvider>
    )
}
